import * as React from 'react';
import PharmacyMarker from './PharmacyMarker';
import { generateCustomIcon } from '../../common/icons';
import { MarkerClusterer } from '@react-google-maps/api';
import { useMemo, useState } from 'react';
import { PharmacyDetails } from '../../store/models';
import { Instance } from 'mobx-state-tree';
import styled from '@emotion/styled';
import { TLCode } from '../../common/enums';
import { TranslatedMessage } from './translation';
import PharmagardeMap from './map/PharmagardeMap';
import { PHARMACY_STATUS_AC } from '../../common/constants/pharmacyStatusConstants';


interface PharmacyMapComponentProps {
  center: google.maps.LatLngLiteral;
  onMapLoad: (map: google.maps.Map) => void;
  pharmacy: Instance<typeof PharmacyDetails>;
  neighbors: any[];
  displayMode: string;
  togglePairingAllowed?: (pharmacyNeighborId) => void;
  toggleExchangeAllowed?: (pharmacyNeighborId) => void;
}

const PharmacyMapComponent = (props: PharmacyMapComponentProps) => {

  const pharmacyIcon = useMemo(
    () => (level: number) => {
      return generateCustomIcon(32, 32, '29B6F6', '29B6F6', level === 3, '000000', false, '888888', '1', '1');
    },
    [],
  );

  const neighborIcon = useMemo(
    () => (level: number) => {
      return generateCustomIcon(16, 16, '888888', '888888', level === 3, '000000', false, '888888', '1', '1');
    },
    [],
  );

  const sameSecretariatNeighborIcon = useMemo(
    () => (level: number) => {
      return generateCustomIcon(16, 16, 'FFCA18', 'FFCA18', level === 3, '000000', false, '888888', '1', '1');
    },
    [],
  );

  const redIcon = useMemo(
    () => (level: number) => {
      return generateCustomIcon(16, 16, 'FF0000', 'FF0000', level === 3, '000000', false, '888888', '1', '1');
    },
    [],
  );

  const greenIcon = useMemo(
    () => (level: number) => {
      return generateCustomIcon(16, 16, '00ff04', '00ff04', level === 3, '000000', false, '888888', '1', '1');
    },
    [],
  );

  const makeIcon = (mode, pharmacy, neighbor) => {
    if (mode === 'std') {
      if (pharmacy.secretariatId === neighbor.secretariatId) {
        return sameSecretariatNeighborIcon(neighbor.level)
      } else {
        return neighborIcon(neighbor.level);
      }
    } else if (mode === 'ex') {
      if (neighbor.exchangeAllowed) {
        return greenIcon(neighbor.level);
      } else {
        if (pharmacy.secretariatId === neighbor.secretariatId) {
          return sameSecretariatNeighborIcon(neighbor.level)
        } else {
          return neighborIcon(neighbor.level);
        }
      }
    } else if (mode === 'co') {
      if (neighbor.pairingAllowed) {
        if (pharmacy.secretariatId === neighbor.secretariatId) {
          return sameSecretariatNeighborIcon(neighbor.level)
        } else {
          return neighborIcon(neighbor.level);
        }
      } else {
        return redIcon(neighbor.level);
      }
    }

  };

  return (
    <>
      <PharmagardeMap
        zoom={8}
        center={props.center}
        options={{ gestureHandling: 'greedy', scaleControl: true, fullscreenControl: true }}
        onLoad={props.onMapLoad}
        mapContainerStyle={{ height: `600px`, width: '100%' }}
      >
        <MarkerClusterer gridSize={20}>
          {(_) => {
            let markers: JSX.Element[] = [];
            markers.push(
              <PharmacyMarker
                key={'Place' + props.pharmacy.pharmacyId}
                id={props.pharmacy.pharmacyId}
                marker={props.pharmacy}
                zIndex={100}
                groupEditMode={false}
                disableRightClick
                icon={pharmacyIcon(props.pharmacy.level)}
              />,
            );

            if (props.neighbors) {
              const neighborsMarkers = props.neighbors
                .filter((i) => i !== undefined && i.status !== undefined && i.status.code === PHARMACY_STATUS_AC)
                .map(
                  (pharmacy) =>

                    <PharmacyMarker
                      key={'Neighbors1' + pharmacy.pharmacyId}
                      id={pharmacy.pharmacyId}
                      marker={pharmacy}
                      pharmacyNeighborId={pharmacy.pharmacyNeighborId}
                      zIndex={100}
                      groupEditMode={false}
                      disableRightClick
                      icon={
                        makeIcon(props.displayMode, props.pharmacy, pharmacy)
                      }
                      onTogglePairingAllowedClick={(id) => props.togglePairingAllowed!(id)}
                      onToggleExchangeAllowedClick={(id) => props.toggleExchangeAllowed!(id)}
                    />
                  ,
                );
              markers = markers.concat(neighborsMarkers);
            }
            return <>{markers}</>;
          }}
        </MarkerClusterer>
      </PharmagardeMap>
      <MapLegend>
        <div>
          <img src={pharmacyIcon(3).url} width="16" height="16" alt={'pharmacyIcon'} />
          <img src={pharmacyIcon(0).url} width="16" height="16" alt={'pharmacyIcon'} />
          <TranslatedMessage id={TLCode.PHARMACY_Map_Legend_pharmacyIcon} />
        </div>
        <div>
          <img src={neighborIcon(3).url} width="16" height="16" alt={'neighborIcon'} />
          <img src={neighborIcon(0).url} width="16" height="16" alt={'neighborIcon'} />
          <TranslatedMessage id={TLCode.PHARMACY_Map_Legend_neighborIcon} />
        </div>
        <div>
          <img src={sameSecretariatNeighborIcon(3).url} width="16" height="16" alt={'sameSecretariatNeighborIcon'} />
          <img src={sameSecretariatNeighborIcon(0).url} width="16" height="16" alt={'sameSecretariatNeighborIcon'} />
          <TranslatedMessage id={TLCode.PHARMACY_Map_Legend_sameSecretariatNeighborIcon} />
        </div>
      </MapLegend>
    </>
  );
};

const MapLegend = styled.div`
  display: flex;
  height: 100%;
  flex-direction: row;
  justify-content: flex-start;
  gap: 20px;
  margin-top: 5px;
  flex: 1;
`;

export default PharmacyMapComponent;
